import type {
    Aggregation,
    Maybe,
    ProductAttributeSortInput
} from '@pylot-data/pylotschema'
import { SortEnum } from '@pylot-data/enums/SortEnum.d'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useReducer } from 'react'
import {
    Action,
    ActionType,
    FiltersStateInput,
    PLPManager,
    PLPState
} from '../FilterTypes.d'

export enum SearchCriteriaFilterActions {
    EQ = 'eq',
    FROM = 'from',
    TO = 'to'
}

export type SearchCriteria = {
    attribute_code: string
    filter_action: SearchCriteriaFilterActions
    filter_value: string
}

export type AppliedFilterData = {
    requestVar: string
    value_string: string
    label: string
}

export const serializeSearchCriteria = (criteria: SearchCriteria[]): string => {
    let serializedCriteria = '',
        skipIteration = false
    criteria.forEach((criterion, index) => {
        if (skipIteration) {
            skipIteration = false
            return ''
        }
        const { filter_action, ...criterionWithoutAction } = criterion
        if (criterionWithoutAction.attribute_code == 'price') {
            if (
                filter_action == SearchCriteriaFilterActions.FROM &&
                criteria[index + 1] &&
                criteria[index + 1].filter_action ==
                    SearchCriteriaFilterActions.TO
            ) {
                skipIteration = true
                serializedCriteria += `${criterionWithoutAction.attribute_code}:`
                serializedCriteria += `${criteria[index].filter_value}_${
                    criteria[index + 1].filter_value
                }-`
                return ''
            }
        }
        serializedCriteria += `${Object.values(criterionWithoutAction)
            .map((str) => str.replace(/[^\w]/gi, '_'))
            .join(':')}-`
    })
    // remove the trailing `-` and return the result
    return serializedCriteria.slice(0, serializedCriteria.length - 1)
}

export const parseSearchCriteria = (criteriaString = ''): SearchCriteria[] => {
    const parsedCriteria: SearchCriteria[] = []
    const commaSeparatedCriteria = criteriaString.split('-')
    commaSeparatedCriteria.forEach((csCriterion) => {
        const values = csCriterion.split(':')
        if (values.length === 2)
            if (values[1].includes('_')) {
                const range = values[1].split('_')
                parsedCriteria.push(
                    {
                        attribute_code: values[0],
                        filter_action: SearchCriteriaFilterActions.FROM,
                        filter_value: range[0]
                    },
                    {
                        attribute_code: values[0],
                        filter_action: SearchCriteriaFilterActions.TO,
                        filter_value: range[1]
                    }
                )
            } else {
                parsedCriteria.push({
                    attribute_code: values[0],
                    filter_action: SearchCriteriaFilterActions.EQ,
                    filter_value: values[1]
                })
            }
    })
    return parsedCriteria
}

export const parseAppliedFilterData = (
    criteriaString = ''
): AppliedFilterData[] => {
    const parsedCriteria: AppliedFilterData[] = []
    const commaSeparatedCriteria = criteriaString.split('-')
    commaSeparatedCriteria.forEach((csCriterion) => {
        const values = csCriterion.split(':')
        if (values.length === 2)
            parsedCriteria.push({
                requestVar: values[0],
                label: values[0],
                value_string: values[1]
            })
    })
    return parsedCriteria
}

export const DEFAULT_CRITERIA_PARAMETER_NAME = 'filter'

const initialState: PLPState = {
    categoryUid: '',
    aggregations: [],
    filters: {},
    appliedFilterData: [],
    sort: {
        position: SortEnum.Asc
    },
    filtersVisible: false,
    appliedSearchCritieria: []
}

const reducer = (state: PLPState, action: Action): PLPState => {
    switch (action.type) {
        case ActionType.CLEAR_PLP_STATE: {
            const payload = action.payload
            return {
                ...initialState,
                ...payload
            }
        }
        case ActionType.SET_CATEGORY_UID: {
            const payload = action.payload
            return {
                ...state,
                categoryUid: payload
            }
        }
        case ActionType.SET_FILTER: {
            const payload = action.payload as FiltersStateInput
            return {
                ...state,
                filters: payload.filters
                    ? {
                          ...state.filters,
                          ...payload.filters
                      }
                    : {},
                appliedFilterData: payload.appliedFilterData
            }
        }
        case ActionType.SET_AGGREGATIONS: {
            const payload = action.payload as Array<Maybe<Aggregation>>
            return {
                ...state,
                aggregations: payload
            }
        }
        case ActionType.SORT: {
            const payload = action.payload as ProductAttributeSortInput
            return {
                ...state,
                sort: payload
            }
        }
        case ActionType.SET_FILTERS_VISIBLE: {
            const payload = action.payload as boolean
            return {
                ...state,
                filtersVisible: payload
            }
        }
        case ActionType.SET_SEARCH_CRITERIA: {
            const payload = action.payload as SearchCriteria[]
            return {
                ...state,
                appliedSearchCritieria: payload
            }
        }
        default:
            return state
    }
}

export const usePLPManager = (
    args: Partial<PLPState> & {
        disableFilterUrlParams?: boolean
        unfilteredAggregations?: Maybe<Maybe<Aggregation>[]>
        criteriaParameterName?: string
    }
): PLPManager => {
    const {
        disableFilterUrlParams = false,
        unfilteredAggregations = initialState.aggregations,
        criteriaParameterName = DEFAULT_CRITERIA_PARAMETER_NAME,
        ...initialStateOverrides
    } = args

    // Used to get and set searchCriteria in the URL parameters
    const router = useRouter()
    const criteriaParam = router.query[criteriaParameterName]

    const criteriaString =
        criteriaParam &&
        typeof criteriaParam === 'string' &&
        criteriaParam.length > 0
            ? criteriaParam
            : typeof window !== 'undefined' &&
              new URLSearchParams(window.location.search).has(
                  criteriaParameterName
              )
            ? new URLSearchParams(window.location.search).get(
                  criteriaParameterName
              )
            : ''
    const arg = {
        ...initialState,
        ...initialStateOverrides,
        appliedSearchCritieria: parseSearchCriteria(criteriaString as string),
        appliedFilterData: parseAppliedFilterData(criteriaString as string),
        aggregations: unfilteredAggregations
    }
    const [plpState, dispatch] = useReducer(reducer, arg)

    const clearPLPState = useCallback(
        (newPLPState) => {
            dispatch({ type: ActionType.CLEAR_PLP_STATE, payload: newPLPState })
        },
        [dispatch]
    )
    const setCategoryUid = useCallback(
        (categoryUid) => {
            dispatch({
                payload: categoryUid,
                type: ActionType.SET_CATEGORY_UID
            })
        },
        [dispatch]
    )
    const setFilter = useCallback(
        (payload) => {
            dispatch({ payload, type: ActionType.SET_FILTER })
        },
        [dispatch]
    )
    const applySort = useCallback(
        (payload) => {
            dispatch({ payload, type: ActionType.SORT })
        },
        [dispatch]
    )
    const setAggregations = useCallback(
        (payload) => {
            dispatch({ payload, type: ActionType.SET_AGGREGATIONS })
        },
        [dispatch]
    )
    const setFiltersVisible = useCallback(
        (payload) => {
            dispatch({ payload, type: ActionType.SET_FILTERS_VISIBLE })
        },
        [dispatch]
    )
    const setSearchCriteria = useCallback(
        (searchCriteriaList) => {
            if (!disableFilterUrlParams && typeof window !== 'undefined') {
                const existingUrl = new URL(window.location.href)
                existingUrl.searchParams.delete(criteriaParameterName)
                const hasOtherParams = existingUrl.search.length > 0
                const hasQuestionMark =
                    searchCriteriaList.length > 0 || hasOtherParams
                const hasAndSign =
                    searchCriteriaList.length > 0 && hasOtherParams

                const newUrlParams =
                    searchCriteriaList.length > 0
                        ? `${criteriaParameterName}=${serializeSearchCriteria(
                              searchCriteriaList
                          )}`
                        : ''

                const newUrl = `${window.location.pathname}${
                    hasQuestionMark ? '?' : ''
                }${newUrlParams}${
                    hasAndSign ? '&' : ''
                }${existingUrl.searchParams.toString()}${window.location.hash}`

                router.push(newUrl, undefined, { shallow: true })
            }
            dispatch({
                payload: searchCriteriaList,
                type: ActionType.SET_SEARCH_CRITERIA
            })
        },
        [criteriaParameterName, disableFilterUrlParams, router]
    )

    useEffect(() => {
        setAggregations(unfilteredAggregations)
    }, [setAggregations, unfilteredAggregations])

    useEffect(() => {
        // `criteriaString` param from the url can be empty on first load - so we need to manually set searchCriteria
        // if we find criteria is set on the second render but searchCriteria is unset
        if (
            router.isReady &&
            criteriaString &&
            plpState.appliedSearchCritieria.length === 0
        ) {
            setSearchCriteria(parseSearchCriteria(criteriaString as string))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.isReady])

    useEffect(() => {
        if (
            JSON.stringify(plpState.appliedSearchCritieria) !=
            JSON.stringify(parseSearchCriteria(criteriaString as string))
        ) {
            setSearchCriteria(parseSearchCriteria(criteriaString as string))
            dispatch({ payload: arg, type: ActionType.SET_FILTER })
        }
    }, [criteriaString])

    return {
        plpState,
        clearPLPState,
        setCategoryUid,
        setFilter,
        setAggregations,
        applySort,
        setFiltersVisible,
        setSearchCriteria
    }
}
